/**
 * Admin shell surfaces from JWT packs / grants.
 * Flags are a thin view over domain × capacity `can()` (PHI-215).
 * Seeded roles keep today’s screens via PACK_TEMPLATE_GRANTS.
 * ops.view alone = viewer consult. ops.view next to a job pack does not widen that job.
 */
import {
  CAPABILITY_PACK,
  packsForRoles,
  type CapabilityPack,
} from './capability-packs.js';
import {
  can,
  grantsFromPacks,
  resolveGrants,
  PERMISSION_DOMAIN,
  type Grant,
} from './permissions.js';

export const GUIDED_FLOWS_ENABLED = false;

const JOB_PACKS: readonly CapabilityPack[] = [
  CAPABILITY_PACK.CATALOG_MAINTAIN,
  CAPABILITY_PACK.OFFER_PRICE,
  CAPABILITY_PACK.STOCK_MOVE,
  CAPABILITY_PACK.BUY_PROCURE,
  CAPABILITY_PACK.BUY_PAY,
  CAPABILITY_PACK.SELL_COMMIT,
  CAPABILITY_PACK.MONEY_READ,
  CAPABILITY_PACK.MONEY_POST,
  CAPABILITY_PACK.PARTY_ADMIN,
];

/** @deprecated Prefer PERMISSION_DOMAIN + can(). Kept for HTTP OR-lists during soak. */
export const DOCUMENT_SEE_PACKS: readonly CapabilityPack[] = [
  CAPABILITY_PACK.SELL_COMMIT,
  CAPABILITY_PACK.STOCK_MOVE,
  CAPABILITY_PACK.BUY_PROCURE,
  CAPABILITY_PACK.BUY_PAY,
  CAPABILITY_PACK.MONEY_READ,
  CAPABILITY_PACK.MONEY_POST,
  CAPABILITY_PACK.OPS_VIEW,
];

/** @deprecated Prefer documents:generate */
export const DOCUMENT_PROCURE_GENERATE_PACKS: readonly CapabilityPack[] = [
  CAPABILITY_PACK.BUY_PROCURE,
  CAPABILITY_PACK.STOCK_MOVE,
];

/** @deprecated Prefer catalog:read */
export const CATALOG_SEE_PACKS: readonly CapabilityPack[] = [
  CAPABILITY_PACK.CATALOG_MAINTAIN,
  CAPABILITY_PACK.OFFER_PRICE,
  CAPABILITY_PACK.SELL_COMMIT,
  CAPABILITY_PACK.STOCK_MOVE,
  CAPABILITY_PACK.BUY_PROCURE,
  CAPABILITY_PACK.OPS_VIEW,
];

/** @deprecated Prefer catalog create/edit/price */
export const CATALOG_WRITE_PACKS: readonly CapabilityPack[] = [
  CAPABILITY_PACK.CATALOG_MAINTAIN,
  CAPABILITY_PACK.OFFER_PRICE,
];

/** @deprecated Prefer customers:read */
export const CUSTOMER_SEE_PACKS: readonly CapabilityPack[] = [
  CAPABILITY_PACK.SELL_COMMIT,
  CAPABILITY_PACK.OPS_VIEW,
];

/** @deprecated Prefer customers:read / notes domain */
export const NOTES_SEE_PACKS: readonly CapabilityPack[] = [
  CAPABILITY_PACK.SELL_COMMIT,
  CAPABILITY_PACK.CATALOG_MAINTAIN,
  CAPABILITY_PACK.OFFER_PRICE,
  CAPABILITY_PACK.OPS_VIEW,
];

/** @deprecated Prefer customers create/edit */
export const NOTES_WRITE_PACKS: readonly CapabilityPack[] = [
  CAPABILITY_PACK.SELL_COMMIT,
  CAPABILITY_PACK.CATALOG_MAINTAIN,
  CAPABILITY_PACK.OFFER_PRICE,
];

/** @deprecated Prefer procurement:read */
export const PROCUREMENT_QUOTE_SEE_PACKS: readonly CapabilityPack[] = [
  CAPABILITY_PACK.BUY_PROCURE,
  CAPABILITY_PACK.BUY_PAY,
  CAPABILITY_PACK.MONEY_READ,
  CAPABILITY_PACK.MONEY_POST,
  CAPABILITY_PACK.OPS_VIEW,
];

export type AdminAccess = {
  seeCatalog: boolean;
  seeInventory: boolean;
  seeOrders: boolean;
  seeCustomers: boolean;
  seeLeads: boolean;
  /** Partnership conventions — partners:read. */
  seeConventions: boolean;
  /** Support tickets desk — support:read (not sell). */
  seeSupport: boolean;
  seeDocuments: boolean;
  seeProcurement: boolean;
  seeAccounting: boolean;
  seeParty: boolean;
  seeStorage: boolean;
  seeSearch: boolean;
  seeDataSources: boolean;
  writeCatalog: boolean;
  writePrice: boolean;
  writeStock: boolean;
  writeBuy: boolean;
  writePay: boolean;
  writeSell: boolean;
  writeMoney: boolean;
  writeParty: boolean;
  writeStorage: boolean;
  /** Home hygiene block: catalog maintain or viewer. */
  homeCatalog: boolean;
  /** Home sales pulse: sell or viewer. */
  homeSales: boolean;
  /** Home B2B recon: sell, stock, buy, or viewer. */
  homeB2bRecon: boolean;
};

function heldPacks(packs: readonly string[] | undefined, roleNames: readonly string[]): Set<CapabilityPack> {
  if (roleNames.includes('SUPER_ADMIN')) return new Set(JOB_PACKS);
  const fromToken = (packs ?? []).filter((p): p is CapabilityPack =>
    (JOB_PACKS as readonly string[]).includes(p) ||
    p === CAPABILITY_PACK.OPS_VIEW ||
    p === CAPABILITY_PACK.STORAGE_READ ||
    p === CAPABILITY_PACK.STORAGE_MANAGE,
  );
  if (fromToken.length > 0) return new Set(fromToken);
  return packsForRoles(roleNames);
}

export function adminAccess(
  packs: readonly string[] | undefined,
  roleNames: readonly string[],
  grants?: readonly string[],
): AdminAccess {
  const isSuper = roleNames.includes('SUPER_ADMIN');
  const packHeld = heldPacks(packs, roleNames);
  const viewerOnly =
    !isSuper &&
    packHeld.size > 0 &&
    [...packHeld].every((p) => p === CAPABILITY_PACK.OPS_VIEW);

  const heldGrants: Set<Grant> = isSuper
    ? resolveGrants({ roleNames: ['SUPER_ADMIN'] })
    : grants?.length
      ? resolveGrants({ grants, roleNames })
      : new Set(grantsFromPacks([...packHeld]));

  const grantList = [...heldGrants];
  const has = (domain: (typeof PERMISSION_DOMAIN)[keyof typeof PERMISSION_DOMAIN], cap: string) =>
    can(grantList, roleNames, domain, cap, packs);

  return {
    seeCatalog: has(PERMISSION_DOMAIN.CATALOG, 'read'),
    seeInventory: has(PERMISSION_DOMAIN.INVENTORY, 'read'),
    seeOrders: isSuper || heldGrants.size > 0,
    seeCustomers: has(PERMISSION_DOMAIN.CUSTOMERS, 'read'),
    seeLeads: has(PERMISSION_DOMAIN.LEADS, 'read'),
    seeConventions: has(PERMISSION_DOMAIN.PARTNERS, 'read'),
    seeSupport: has(PERMISSION_DOMAIN.SUPPORT, 'read'),
    seeDocuments: has(PERMISSION_DOMAIN.DOCUMENTS, 'read'),
    seeProcurement: has(PERMISSION_DOMAIN.PROCUREMENT, 'read'),
    seeAccounting: has(PERMISSION_DOMAIN.ACCOUNTING, 'read'),
    seeParty: has(PERMISSION_DOMAIN.PEOPLE, 'read'),
    seeStorage: has(PERMISSION_DOMAIN.STORAGE, 'read'),
    seeSearch:
      has(PERMISSION_DOMAIN.CATALOG, 'edit') ||
      has(PERMISSION_DOMAIN.CATALOG, 'price') ||
      has(PERMISSION_DOMAIN.SALES, 'commit') ||
      viewerOnly,
    seeDataSources: has(PERMISSION_DOMAIN.CATALOG, 'edit') || has(PERMISSION_DOMAIN.CATALOG, 'create'),
    writeCatalog: has(PERMISSION_DOMAIN.CATALOG, 'edit') || has(PERMISSION_DOMAIN.CATALOG, 'create'),
    writePrice: has(PERMISSION_DOMAIN.CATALOG, 'price'),
    writeStock: has(PERMISSION_DOMAIN.INVENTORY, 'move'),
    writeBuy: has(PERMISSION_DOMAIN.PROCUREMENT, 'order'),
    writePay: has(PERMISSION_DOMAIN.PROCUREMENT, 'pay'),
    writeSell: has(PERMISSION_DOMAIN.SALES, 'commit'),
    writeMoney: has(PERMISSION_DOMAIN.ACCOUNTING, 'post'),
    writeParty: has(PERMISSION_DOMAIN.PEOPLE, 'edit') || has(PERMISSION_DOMAIN.PEOPLE, 'create'),
    writeStorage: has(PERMISSION_DOMAIN.STORAGE, 'upload'),
    homeCatalog: has(PERMISSION_DOMAIN.CATALOG, 'edit') || has(PERMISSION_DOMAIN.CATALOG, 'create') || viewerOnly,
    homeSales: has(PERMISSION_DOMAIN.SALES, 'commit') || viewerOnly,
    homeB2bRecon:
      has(PERMISSION_DOMAIN.SALES, 'commit') ||
      has(PERMISSION_DOMAIN.INVENTORY, 'move') ||
      has(PERMISSION_DOMAIN.PROCUREMENT, 'order') ||
      viewerOnly,
  };
}
